/** Types mirrored from the server. Kept narrow — only what the UI renders. */

import type { Block } from "./blocks";
export type { Block };

export type EngineEvent =
  | { type: "start"; sessionId: string; model: string }
  | { type: "text"; delta: string }
  | { type: "thinking"; delta: string }
  | { type: "tool_start"; id: string; name: string; input?: unknown }
  | { type: "tool_input"; id: string; input: unknown }
  | { type: "tool_end"; id: string; name: string; ok: boolean; output?: string }
  | { type: "retry"; attempt: number; max: number; delayMs: number; reason: string }
  | { type: "done"; sessionId: string; stopReason: string; usage?: Usage }
  | { type: "error"; message: string; recoverable: boolean };

export type Usage = { inputTokens?: number; outputTokens?: number; costUsd?: number };

export type LiveEvent = { i: number; convId: string; msgId: string; evt: EngineEvent };

export type ToolCall = {
  id: string;
  toolId: string;
  name: string;
  status: "running" | "ok" | "failed";
  input: unknown;
  output: string;
};

export type Attachment = {
  id?: string;
  path: string;
  name: string;
  mime: string;
  bytes?: number;
};

export type MessageStatus = "streaming" | "complete" | "stopped" | "error" | "interrupted";

export type Message = {
  id: string;
  convId: string;
  seq: number;
  role: "user" | "assistant";
  content: string;
  thinking: string;
  status: MessageStatus;
  error: string;
  engine: string;
  model: string;
  usage: Usage | null;
  createdAt: number;
  tools: ToolCall[];
  attachments: Attachment[];
  /** Ordered interleaved content. Empty on pre-migration rows until synthesized. */
  blocks: Block[];
};

export type Conversation = {
  id: string;
  title: string;
  engine: string;
  engineSessionId: string;
  model: string;
  createdAt: number;
  updatedAt: number;
  archived: boolean;
  buildMode: boolean;
};

export type ConversationSummary = Conversation & { messageCount: number; preview: string };

export type EngineInfo = {
  id: string;
  label: string;
  available: boolean;
  detail: string;
  models: string[];
  defaultModel: string;
};

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

type UnauthorizedFn = () => void;
const unauthorizedListeners = new Set<UnauthorizedFn>();

export function onUnauthorized(fn: UnauthorizedFn): () => void {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

export function reportUnauthorized(): void {
  for (const fn of unauthorizedListeners) fn();
}

/** Same-origin fetch that flips the app to login on any 401. */
export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(path, { credentials: "same-origin", ...init });
  if (res.status === 401) reportUnauthorized();
  return res;
}

export type PasskeySummary = {
  id: string;
  name: string;
  backedUp: boolean;
  createdAt: number;
  lastUsed: number | null;
};

async function request<T>(path: string, init?: RequestInit & { timeoutMs?: number }): Promise<T> {
  const ctrl = new AbortController();
  const timeout = setTimeout(() => ctrl.abort(), init?.timeoutMs ?? 15_000);
  try {
    const res = await apiFetch(path, {
      ...init,
      signal: init?.signal ?? ctrl.signal,
      // No JSON content-type without a body: Fastify answers 400 to that,
      // which is how Sign out silently failed to end the server session.
      headers:
        init?.body instanceof FormData || init?.body == null
          ? init?.headers
          : { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    });
    if (!res.ok) {
      // Server errors carry {error}. Fall back to the status line if not.
      let detail = res.statusText;
      try {
        const body = (await res.json()) as { error?: string };
        if (body?.error) detail = body.error;
      } catch {
        /* non-JSON error body */
      }
      throw new ApiError(detail, res.status);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  } catch (e) {
    if (e instanceof ApiError) throw e;
    if (e instanceof DOMException && e.name === "AbortError") {
      throw new ApiError("request timed out", 408);
    }
    throw new ApiError(e instanceof Error ? e.message : String(e), 0);
  } finally {
    clearTimeout(timeout);
  }
}

export const api = {
  health: () => request<{ ok: boolean; version: number }>("/api/health"),

  authStatus: () =>
    request<{ authenticated: boolean; passkey?: { available: boolean; required: boolean; count: number } }>(
      "/api/auth/status",
    ),

  passkeys: () =>
    request<{ passkeys: PasskeySummary[]; available: boolean; enabled: boolean; origin: string }>(
      "/api/auth/passkeys",
    ),

  /** Face ID / Touch ID / Windows Hello prompt, then store the new passkey. */
  addPasskey: async (name?: string) => {
    const { startRegistration } = await import("@simplewebauthn/browser");
    const optionsJSON = await request<Parameters<typeof startRegistration>[0]["optionsJSON"]>(
      "/api/auth/passkey/register-options",
      { method: "POST", body: "{}" },
    );
    const response = await startRegistration({ optionsJSON });
    return request<{ ok: boolean; passkeys: PasskeySummary[] }>("/api/auth/passkey/register-verify", {
      method: "POST",
      body: JSON.stringify({ response, name }),
    });
  },

  removePasskey: (id: string) =>
    request<{ ok: boolean; passkeys: PasskeySummary[] }>(`/api/auth/passkeys/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  login: (password: string) =>
    request<{ ok: boolean }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ password }),
    }),

  logout: async () => {
    try {
      await request<{ ok: boolean }>("/api/auth/logout", { method: "POST" });
    } finally {
      reportUnauthorized();
    }
  },

  engines: (refresh = false) =>
    request<{ engines: EngineInfo[]; order: string[] }>(
      `/api/engines${refresh ? "?refresh=1" : ""}`,
      { timeoutMs: 40_000 },
    ),

  conversations: (archived = false) =>
    request<{ conversations: ConversationSummary[] }>(
      `/api/conversations?archived=${archived ? 1 : 0}`,
    ),

  conversation: (id: string) =>
    request<{
      conversation: Conversation;
      messages: Message[];
      live: { msgId: string; nextIndex: number } | null;
    }>(`/api/conversations/${id}`),

  createConversation: (engine?: string, model?: string) =>
    request<{ conversation: Conversation }>("/api/conversations", {
      method: "POST",
      body: JSON.stringify({ engine, model }),
    }),

  renameConversation: (id: string, title: string) =>
    request<{ conversation: Conversation }>(`/api/conversations/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ title }),
    }),

  archiveConversation: (id: string, archived: boolean) =>
    request<{ conversation: Conversation }>(`/api/conversations/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ archived }),
    }),

  setBuildMode: (id: string, buildMode: boolean) =>
    request<{ conversation: Conversation }>(`/api/conversations/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ buildMode }),
    }),

  deleteConversation: (id: string) =>
    request<void>(`/api/conversations/${id}`, { method: "DELETE" }),

  send: (body: {
    convId?: string;
    message: string;
    engine?: string;
    model?: string;
    attachments?: Attachment[];
    retryFromSeq?: number;
    buildMode?: boolean;
  }) =>
    request<{ convId: string; msgId: string }>("/api/chat/send", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  stop: (convId: string) =>
    request<{ stopped: boolean }>("/api/chat/stop", {
      method: "POST",
      body: JSON.stringify({ convId }),
    }),

  upload: (files: File[]) => {
    const fd = new FormData();
    for (const f of files) fd.append("files", f, f.name);
    return request<{ attachments: Attachment[] }>("/api/uploads", {
      method: "POST",
      body: fd,
      timeoutMs: 120_000,
    });
  },

  search: (q: string) =>
    request<{ results: { convId: string; msgId: string; snippet: string }[] }>(
      `/api/search?q=${encodeURIComponent(q)}`,
    ),
};

/**
 * SSE subscription with automatic resume.
 *
 * `EventSource` cannot send headers but it reconnects on its own — and because
 * the server replays from `since`, a reconnect is lossless. We rebuild the URL
 * on each retry so the resume index is always current.
 */
export function streamTurn(
  convId: string,
  getSince: () => number,
  handlers: {
    onEvent: (e: LiveEvent) => void;
    onIdle?: () => void;
    onStatus?: (connected: boolean) => void;
  },
): () => void {
  let source: EventSource | null = null;
  let retry: ReturnType<typeof setTimeout> | null = null;
  let closed = false;
  let backoff = 500;

  const connect = () => {
    if (closed) return;
    source = new EventSource(`/api/chat/stream?convId=${convId}&since=${getSince()}`, {
      withCredentials: true,
    });

    source.addEventListener("open", () => {
      backoff = 500;
      handlers.onStatus?.(true);
    });

    source.addEventListener("turn", (ev) => {
      try {
        handlers.onEvent(JSON.parse((ev as MessageEvent<string>).data) as LiveEvent);
      } catch {
        /* ignore a torn frame; the next one carries a higher index */
      }
    });

    source.addEventListener("idle", () => handlers.onIdle?.());

    source.addEventListener("error", () => {
      handlers.onStatus?.(false);
      source?.close();
      source = null;
      if (closed) return;
      // EventSource does not expose the HTTP status. Probe auth so a 401
      // cannot spin the existing reconnect loop behind the login screen.
      void (async () => {
        try {
          const res = await fetch("/api/auth/status", { credentials: "same-origin" });
          if (res.status === 401) {
            reportUnauthorized();
            return;
          }
          const body = res.ok ? ((await res.json()) as { authenticated?: boolean }) : null;
          if (!body?.authenticated) {
            reportUnauthorized();
            return;
          }
        } catch {
          /* network blip — reconnect as before */
        }
        if (closed) return;
        retry = setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, 8000);
      })();
    });
  };

  connect();

  return () => {
    closed = true;
    if (retry) clearTimeout(retry);
    source?.close();
  };
}
